import { useState } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCustomerUsers,
  useSetCustomerClients,
  useDeactivateCustomer,
  useReactivateCustomer,
  useCustomerInvitations,
  useRevokeCustomerInvitation,
  useResendCustomerInvitation,
} from "@/hooks/useCustomers";
import { useClients } from "@/hooks/useClients";
import { useOrganization } from "@/hooks/useOrganization";
import { Users, UserPlus, Mail, UserX, RefreshCw, X, Trash2, RotateCcw } from "lucide-react";
import { InviteCustomerDialog } from "@/components/customers/InviteCustomerDialog";
import { CustomerClientsPicker } from "@/components/customers/CustomerClientsPicker";
import { formatDistanceToNow } from "date-fns";

// ─── Main Page ───────────────────────────────────────────────────────────

export default function Customers() {
  const [showInvite, setShowInvite] = useState(false);

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Customers</h1>
        </div>
        <Button size="sm" onClick={() => setShowInvite(true)}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          Invite Customer
        </Button>
      </header>

      <InviteCustomerDialog open={showInvite} onClose={() => setShowInvite(false)} />

      <div className="flex-1 p-6">
        <Tabs defaultValue="active" className="w-full space-y-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Active
            </TabsTrigger>
            <TabsTrigger value="inactive" className="flex items-center gap-2">
              <UserX className="h-4 w-4" />
              Inactive
            </TabsTrigger>
            <TabsTrigger value="invites" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Invites
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <CustomerUsersTab active />
          </TabsContent>
          <TabsContent value="inactive">
            <CustomerUsersTab active={false} />
          </TabsContent>
          <TabsContent value="invites">
            <CustomerInvitesTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── Customer Users Tab (active or inactive) ────────────────────────────

interface CustomerAccessFields {
  id: string;
  org_id: string;
  client_id: string | null;
  portal_client_access?: { client_id: string }[] | null;
}

/** Client ids a customer login can see (portal_client_access). */
function accessClientIds(user: CustomerAccessFields): string[] {
  return (user.portal_client_access ?? []).map((a) => a.client_id);
}

function CustomerUsersTab({ active }: { active: boolean }) {
  const { data: customers = [], isLoading } = useCustomerUsers(active);
  const { data: allClients = [] } = useClients();
  const { data: org } = useOrganization();
  const setClients = useSetCustomerClients();

  // Filter out the organization's own client entry — customers should only be linked to external clients
  const clients = (allClients as any[]).filter(
    (c: any) => !org?.name || c.name !== org.name
  );
  const clientNames = new Map<string, string>(clients.map((c: any) => [c.id, c.name]));
  const deactivate = useDeactivateCustomer();
  const reactivate = useReactivateCustomer();
  const [query, setQuery] = useState("");

  const filtered = query
    ? customers.filter((u: any) => {
        const q = query.toLowerCase();
        return (
          u.display_name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          accessClientIds(u).some((id) => clientNames.get(id)?.toLowerCase().includes(q))
        );
      })
    : customers;

  function handleSetClients(user: CustomerAccessFields, clientIds: string[]) {
    setClients.mutate({
      userId: user.id,
      orgId: user.org_id,
      primaryClientId: user.client_id ?? null,
      currentClientIds: accessClientIds(user),
      clientIds,
    });
  }

  const emptyMessage = active
    ? query ? `No customers match "${query}"` : "No active customer users yet."
    : query ? `No customers match "${query}"` : "No inactive customers.";

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <Input
          placeholder="Search by name, email, or client…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-80"
        />
        <span className="text-sm text-muted-foreground">
          {filtered.length} customer{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="border rounded-lg bg-card">
        {isLoading ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Clients</TableHead>
                <TableHead>Signed Up</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((user: any) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.display_name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <CustomerClientsPicker
                      clients={clients}
                      selectedIds={accessClientIds(user)}
                      disabled={setClients.isPending}
                      onSave={(ids) => handleSetClients(user, ids)}
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(user.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    {active ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove customer?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will deactivate {user.display_name || user.email}'s account. They will no longer be able to log in. You can reactivate them later from the Inactive tab.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deactivate.mutate(user.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reactivate.mutate(user.id)}
                        disabled={reactivate.isPending}
                        title="Reactivate customer"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  );
}

// ─── Customer Invites Tab ───────────────────────────────────────────────

function CustomerInvitesTab() {
  const { data: invitations = [], isLoading } = useCustomerInvitations();
  const resend = useResendCustomerInvitation();
  const revoke = useRevokeCustomerInvitation();

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading invitations...</div>;
  }

  const pending = invitations.filter((inv: any) => inv.status === "pending");

  if (pending.length === 0) {
    return (
      <div className="border rounded-lg bg-card">
        <div className="text-center py-8 text-muted-foreground">No pending customer invitations.</div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Invited By</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pending.map((invitation: any) => {
            const isExpired = new Date(invitation.expires_at) < new Date();
            return (
              <TableRow key={invitation.id}>
                <TableCell className="font-medium">{invitation.email}</TableCell>
                <TableCell className="text-muted-foreground">
                  {invitation.client?.name || "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {invitation.invited_by?.display_name || invitation.invited_by?.email || "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
                </TableCell>
                <TableCell>
                  <Badge variant={isExpired ? "secondary" : "default"}>
                    {isExpired ? "Expired" : "Pending"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resend.mutate(invitation.id)}
                      disabled={resend.isPending}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will cancel the invitation to {invitation.email}. They will no longer be able to use this invite link.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => revoke.mutate(invitation.id)}>
                            Revoke
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

